import { ImageUpload } from '@/components/ImageUpload'
import { SectionCard } from '@/components/SectionCard'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect, type SearchableSelectGroup } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { useGetDepartmentOptions } from '@/features/departments/hooks/useDepartment'
import { useFileUploadField } from '@/hooks/useFileUpload'
import { isApiError } from '@/utils/error'
import { getMutationOptions } from '@/utils/mutation'
import { showError } from '@/utils/toast'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetConfigs, useUpdateConfig } from '../hooks/useConfig'
import { APP_CONFIG_FIELDS, AppConfig, AppConfigKey, AppConfigMap, defaultAppConfig } from '../types/config'

export function SettingsView() {
  const { t } = useTranslation()
  const { data: configs, isFetching: isFetchingConfigs } = useGetConfigs(true)
  const { data: departments, isFetching: isFetchingDepartments } = useGetDepartmentOptions()
  const updateConfig = useUpdateConfig()

  const [isEditing, setIsEditing] = useState(false)
  const [form, setForm] = useState<AppConfigMap>(defaultAppConfig)
  const [original, setOriginal] = useState<AppConfigMap>(defaultAppConfig)

  const warehouseDepartmentId =
    configs?.[AppConfigKey.WarehouseDepartmentId] ?? form[AppConfigKey.WarehouseDepartmentId]
  const departmentGroups = useMemo<SearchableSelectGroup[]>(() => {
    const departmentOptions =
      departments?.map(department => ({
        value: department.id,
        label: department.name,
        description: [department.buildingName, department.floorName].filter(Boolean).join(' - '),
        searchValue: `${department.name} ${department.buildingName || ''} ${department.floorName || ''}`,
      })) ?? []

    return [
      {
        label: t('scans.mainWarehouse'),
        options: departmentOptions.filter(department => department.value === warehouseDepartmentId),
      },
      {
        label: t('scans.otherDepartments'),
        options: departmentOptions.filter(department => department.value !== warehouseDepartmentId),
      },
    ]
  }, [departments, t, warehouseDepartmentId])

  const imageFields: Partial<Record<AppConfigKey, ReturnType<typeof useFileUploadField>>> = {
    [AppConfigKey.HospitalLogo]: useFileUploadField({
      value: form[AppConfigKey.HospitalLogo] ? [form[AppConfigKey.HospitalLogo]] : [],
      onChange: ids =>
        setForm(prev => ({
          ...prev,
          [AppConfigKey.HospitalLogo]: ids[0] || '',
        })),
      maxFiles: 1,
      onUploadError: error => {
        if (isApiError(error)) showError(error.message)
      },
    }),

    [AppConfigKey.HospitalMap]: useFileUploadField({
      value: form[AppConfigKey.HospitalMap] ? [form[AppConfigKey.HospitalMap]] : [],
      onChange: ids =>
        setForm(prev => ({
          ...prev,
          [AppConfigKey.HospitalMap]: ids[0] || '',
        })),
      maxFiles: 1,
      onUploadError: error => {
        if (isApiError(error)) showError(error.message)
      },
    }),
  }

  useEffect(() => {
    if (configs) {
      setForm(configs)
      setOriginal(configs)
    }
  }, [configs])

  function handleSave() {
    const options = getMutationOptions({
      successMessage: t('common.setting.toast.editSuccess'),
      onSuccess: () => {
        setIsEditing(false)
      },
    })

    const payload: AppConfig[] = Object.entries(form).map(([key, value]) => ({
      key: key as AppConfigKey,
      value,
    }))

    updateConfig.mutate({ payload }, options)
  }

  function getDepartmentLabel(departmentId: string) {
    return departments?.find(d => d.id === departmentId)?.name
  }

  const LANGUAGE_OPTIONS = [
    { value: 'th', label: 'ไทย' },
    { value: 'en', label: 'English' },
  ] as const

  function getLanguageLabel(language: string) {
    return LANGUAGE_OPTIONS.find(l => l.value === language)?.label
  }

  const isLoading = isFetchingConfigs || isFetchingDepartments
  const isUploadingImages =
    imageFields[AppConfigKey.HospitalLogo]?.isUploading || imageFields[AppConfigKey.HospitalMap]?.isUploading

  return (
    <SectionCard
      isLoading={isLoading}
      title={t('common.setting.title')}
      right={
        <>
          {!isEditing ? (
            <Button data-testid="settings-edit-button" onClick={() => setIsEditing(true)} size="sm" className="h-8 text-xs" disabled={isLoading}>
              {t('common.edit')}
            </Button>
          ) : (
            <>
              <Button
                data-testid="settings-cancel-button"
                variant="outline"
                onClick={() => {
                  setForm(original)
                  setIsEditing(false)
                }}
                size="sm"
                className="h-8 text-xs"
              >
                {t('common.cancel')}
              </Button>
              <Button
                data-testid="settings-save-button"
                onClick={() => {
                  handleSave()
                }}
                size="sm"
                className="h-8 text-xs"
                disabled={updateConfig.isPending || isUploadingImages}
              >
                {updateConfig.isPending && <Spinner />}
                {t('common.save')}
              </Button>
            </>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {APP_CONFIG_FIELDS.map(field => {
          const value = form[field.key]
          const img = imageFields[field.key]

          return (
            <div key={field.key} className="flex flex-col gap-1">
              {/* Label */}
              <Label className="text-xs text-muted-foreground">{t(field.labelKey)}</Label>

              {/* VIEW MODE */}
              {!isEditing && (
                <>
                  {field.type === 'text' && <div className="text-sm text-foreground">{value || '-'}</div>}

                  {field.type === 'textarea' && (
                    <div className="whitespace-pre-wrap text-sm text-foreground">{value || '-'}</div>
                  )}

                  {field.type === 'image' &&
                    (value ? (
                      <ImageUpload files={[]} imageIds={[value]} onChange={() => {}} maxImages={1} readonly />
                    ) : (
                      <div className="text-sm text-muted-foreground">-</div>
                    ))}

                  {field.type === 'select' && field.key === AppConfigKey.WarehouseDepartmentId && (
                    <div className="text-sm text-foreground">{getDepartmentLabel(value) ?? '-'}</div>
                  )}

                  {field.type === 'select' && field.key === AppConfigKey.DefaultLanguage && (
                    <div className="text-sm text-foreground">{getLanguageLabel(value) ?? '-'}</div>
                  )}
                </>
              )}

              {/* EDIT MODE */}
              {isEditing && (
                <>
                  {/* TEXT */}
                  {field.type === 'text' && (
                    <Input
                      data-testid={`settings-input-${field.key}`}
                      value={value}
                      onChange={e =>
                        setForm(prev => ({
                          ...prev,
                          [field.key]: e.target.value,
                        }))
                      }
                      className="h-9 bg-secondary/50 text-xs"
                    />
                  )}

                  {/* TEXTAREA */}
                  {field.type === 'textarea' && (
                    <Textarea
                      data-testid={`settings-textarea-${field.key}`}
                      value={value}
                      onChange={e =>
                        setForm(prev => ({
                          ...prev,
                          [field.key]: e.target.value,
                        }))
                      }
                      className="bg-secondary/50 text-xs"
                      rows={2}
                    />
                  )}

                  {/* SELECT */}
                  {field.type === 'select' && field.key === AppConfigKey.WarehouseDepartmentId && (
                    <SearchableSelect
                      testId={`settings-select-${field.key}`}
                      value={value}
                      onValueChange={departmentId =>
                        setForm(prev => ({
                          ...prev,
                          [field.key]: departmentId,
                        }))
                      }
                      groups={departmentGroups}
                      placeholder=""
                      searchPlaceholder={t('common.placeholder.searchDepartment')}
                      emptyText={t('common.noDepartment')}
                      triggerClassName="bg-secondary/50 text-xs"
                    />
                  )}

                  {field.type === 'select' && field.key === AppConfigKey.DefaultLanguage && (
                    <Select
                      value={value}
                      onValueChange={v =>
                        setForm(prev => ({
                          ...prev,
                          [field.key]: v,
                        }))
                      }
                    >
                      <SelectTrigger data-testid={`settings-select-${field.key}`} className="h-9 w-full bg-secondary/50 text-xs">
                        <SelectValue />
                      </SelectTrigger>

                      <SelectContent>
                        {LANGUAGE_OPTIONS.map(option => (
                          <SelectItem data-testid={`settings-select-${field.key}-option-${option.value}`} key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {/* IMAGE */}
                  {field.type === 'image' && img && (
                    <ImageUpload
                      testId={`settings-image-${field.key}`}
                      files={img.files}
                      // imageUrls={img.imageUrls}
                      imageIds={img.imageIds}
                      onChange={files => img.handleChange(files)}
                      onRemoveExisting={img.handleRemoveExisting}
                      maxImages={1}
                    />
                  )}
                </>
              )}
            </div>
          )
        })}
      </div>
    </SectionCard>
  )
}
